import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = BACKEND_URL ? `${BACKEND_URL}/api` : "/api"; // same-origin (Netlify) when unset

export const api = axios.create({ baseURL: API, withCredentials: false });

// Always attach the current token. Reading localStorage on every request is
// intentional — it avoids the token going stale when it's rotated in another
// tab or refreshed after re-login.
api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem("gumble_token");
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

// Only clear the token when the server itself says the token is invalid
// (401). Everything else (5xx, network blips, backend restarting) MUST NOT
// clear the session — otherwise "not authenticated" errors during a transient
// backend hiccup boot the user for the whole tab and never let them back in.
api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response && err.response.status === 401) {
      const url = err.config?.url || "";
      // Only nuke the token on 401s that come from auth-critical endpoints.
      // A one-off 401 from e.g. /bets/sync during a backend blip shouldn't
      // sign the user out globally.
      if (url.includes("/auth/me") || url.includes("/auth/logout")) {
        localStorage.removeItem("gumble_token");
      }
    }
    return Promise.reject(err);
  }
);

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const t = localStorage.getItem("gumble_token");
    if (!t) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      if (data && data.user) {
        setUser(data.user);
      } else {
        // Server explicitly says no user for this token — safe to clear.
        localStorage.removeItem("gumble_token");
        setUser(null);
      }
    } catch (e) {
      // Network / server hiccup: keep the token, keep whatever user state we
      // already had. The next successful call will reconcile.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Re-check auth when the tab comes back into focus — this catches the case
  // where the backend restarted while the tab was in the background.
  useEffect(() => {
    const onFocus = () => { refresh(); };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refresh]);

  const login = () => {
    window.location.href = `${API}/auth/discord/login`;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("gumble_token");
    setUser(null);
  };

  return (
    <AuthCtx.Provider value={{ user, loading, refresh, login, logout }}>{children}</AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
export { API, BACKEND_URL };
